import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { OrderEngineContext } from './OrderEngineContext'
import { OkeDeviceContext } from './OkeDeviceContext'
import { OkeFrameContext } from './OkeFrameContext'
import { brandPrinters } from '../api/brandPrinters'
import { strCenter, strLeftRight, strRight, wordWrap } from '../helpers/strings'
import cloneDeep from 'lodash.clonedeep'
import moment from 'moment'
import { UUID } from "uuidjs"
import okeResponse from 'okeoke.standardresponse'
import { OrderStatus } from '../orderengine-client/src/consts'
import figlet from 'figlet'
import figletFont from 'figlet/importable-fonts/Standard.js'

const OkePrintContext = createContext()

const payInType = {
  Change: 0,
  Cashier: 1,
  FeeCollection: 2,
  Lottery: 3,
  Deposit: 4,
  Missing: 5,
  Tip: 6,
  Other: 7
}

const payInTypeNames = {
  0: 'Váltópénz',
  1: 'Pénztár',
  2: 'Díjbeszedés',
  3: 'Lottó',
  4: 'Letét',
  5: 'Hiány',
  6: 'Borravaló',
  7: 'Egyéb'
}

const payOutType = {
  Skim: 0,
  Cashier: 1,
  Ticket: 2,
  GiftCard: 3,
  Sallary: 4,
  Advance: 5,
  Post: 6,
  OtherFee: 7,
  BuyGoods: 8,
  TotalPayOut: 9,
  OtherPayOut: 10
}

const payOutTypeNames = {
  0: 'Kasszazárás',
  1: 'Pénztár',
  2: 'Jegy',
  3: 'Ajándékutalvány',
  4: 'Fizetés',
  5: 'Előleg',
  6: 'Posta',
  7: 'Egyéb díj',
  8: 'Áruvásárlás',
  9: 'Összes kiadás',
  10: 'Egyéb kiadás'
}

const agentTypeNames = {
  'cash': 'Készpénz',
  'bankCard': 'Bankkártya',
  'online': 'Online',
  'voucher': 'Voucher',
  'other': 'Egyéb',
  'cashEuro': 'Euro készpénz',
  'szepkartya': 'Szépkártya',
  'transfer': 'Átutalás',
  'credit': 'Hitel',
  'vip': 'Vip voucher',
  'wolt': 'Wolt',
  'foodora': 'Foodora'
}

const stornoReasonType = {
  Customer: 1,
  ItemMistake: 2,
  PaymentMistake: 3,
  ItemNoStock: 4,
  ReceiptTypeMistake: 5,
  PaymentUnsuccessful: 6,
  UserDateMistake: 7,
  PracticeSale: 8,
  Other: 0
}

const deviceTypeName = {
  1: 'Kiosk',
  2: 'OM',
  3: 'Pos',
  6: 'Ecr',
  1000: 'App',
  1001: 'Wolt',
  1002: 'Foodora'
}

const vats = {
  '5.00': 'A',
  '18.00': 'B',
  '27.00': 'C',
  '0.00': 'E',
  5: 'A',
  18: 'B',
  27: 'C',
  0: 'E'
}

const sizeUnitNames = {
  'piece': 'db',
  'unit': 'egys.',
  'liter': 'l',
  'kg': 'kg'
}

const paymentBlockNames = {
  'cash': 'Készpénz',
  'bankCard': 'Bankkártya',
  'online': 'Online',
  'voucher': 'Voucher',
  'other': 'Más',
  'cashEuro': 'Készpénz Euro',
  'szepkartya': 'Szépkártya',
  'transfer': 'Átutalás',
  'credit': 'Hitel',
  'vip': 'Vip voucher',
  'wolt': 'Wolt',
  'foodora': 'Foodora'
}

const saleItemTypes = {
  0: 'Termék',
  1: 'Szervíz díj',
  3: 'Csomagolás',
  4: 'Kedvezmény',
  5: 'Szállítási díj',
  6: 'Borravaló',
  7: 'Betétdíj',
}

const unitTypeFixed = {
  'piece': 0,
  'unit': 0,
  'liter': 3,
  'kg': 3
}

function getPreferredLanguage(name, langCode, nameFallback = null) {
  let lang = name?.[langCode]
  if (lang == null) {
    lang = name?.['hu'] || name?.['en'] || nameFallback
  }
  return lang || ''
}

const OkePrintProvider = (props) => {
  const uid = useRef(UUID.generate())
  const [printers, setPrinters] = useState(null)
  const [location, setLocation] = useState([])
  const printersRef = useRef(null)
  const { client, locations } = useContext(OrderEngineContext)
  const { hasDeviceContext, deviceParams, loggedIn, brandID: deviceBrandID, deviceType, token: deviceToken,
    deviceUUID, apiUrl: deviceApiUrl, deviceLocationUUID, deviceName, loggedInUser, devicePaymentAgents } = useContext(OkeDeviceContext) || {}
  const { sendMessageToWebView } = useContext(OkeFrameContext) || {}
  const currentlyPrinting = useRef({})
  const extOrderNumberFiscalPrintCB = useRef(null)

  const setExtOrderNumberFiscalPrintCB = (cb) => {
    extOrderNumberFiscalPrintCB.current = cb
  }

  const onPrintersUpdated = (data) => {
    if (data?.printer?.deviceUUID === deviceUUID || data?.printer?.locationUUID === deviceLocationUUID) {
      brandPrinters.getDevicePrinters(deviceApiUrl, deviceLocationUUID, deviceUUID).then(res => {
        if (res.success) {
          setPrinters(res.data)
        }
      })
    }
  }
  //strCenter, strLeftRight, strRight, wordWrap
  const formatLocationAddressPrintSlip = (maxChars = 32) => {
    let res = [
      { bold: false, Msg: strCenter(`${location.address?.zip} ${location.address?.city}`, maxChars) },
      { bold: false, Msg: strCenter(`${location.address?.street} ${location.address?.streetNumber} ${location.address?.floor}`, maxChars) },
    ]
    return res
  }

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

  const showDigital = async (line1) => {
    let res = await sendMessageToWebView({
      action: 'sunmiShowDigital',
      data: { line1 }
    })
    return (res?.data || { success: false, code: 500, message: 'Internal error' })
  }

  const waitIfPrinting = async (printerUUID) => {
    if (currentlyPrinting.current[printerUUID]) {
      await delay(1000)
      await waitIfPrinting()
    }
  }

  const posPrint = async (printSlip, printerUUID = null) => {
    let { printerType = 'EPSON', commType = 'net', commPath = '0.0.0.0:9100', maxChars = 32 } =
      printerUUID != null ? printersRef.current?.[printerUUID] || {} : printersRef.current?.[deviceParams?.posPrinter?.uuid] || {}
    await delay(250)
    await waitIfPrinting(printerUUID || deviceParams?.posPrinter?.uuid)
    currentlyPrinting.current[printerUUID || deviceParams?.posPrinter?.uuid] = true
    let res = await sendMessageToWebView({
      action: 'escPosPrint',
      data: { printerType, commType, commPath, printSlip, maxChars }
    })
    currentlyPrinting.current[printerUUID || deviceParams?.posPrinter?.uuid] = false
    return (res?.data || { success: false, code: 500, message: 'Internal error' })
  }

  const addressPrintSlip = (address, options = {}, justInfo = false) => {
    if (address?.street == null) return []
    let { maxChars = 32 } = options || {}
    let {
      zip,
      city,
      name,
      floor,
      street,
      telNumber,
      streetNumber,
      notes
    } = address
    let res = []
    if (!justInfo) {
      res.push({ Bold: true, Msg: '%%% HÁZHOZ! %%%', Font: 'double' })
    } else {
      res.push({ Bold: false, Msg: 'Szállítási cím:' })
    }
    res.push({ Bold: false, Msg: strLeftRight(`Név:`, `${name}`, maxChars) })
    res.push({ Bold: false, Msg: strLeftRight(`Tel:`, `${telNumber}`, maxChars) })
    res.push({ Bold: false, Msg: strLeftRight(`Cím:`, `${zip} ${city}`, maxChars) })
    res.push({ Bold: false, Msg: strRight(`${street} ${streetNumber} ${floor}`, maxChars) })
    if (notes?.length > 0) {
      res.push({ Bold: false, Msg: wordWrap(`${notes}`, maxChars) })
    }
    return res
  }

  const createPrintSlip = (orderUUID, options = {}, _orderObj = null) => {
    let order = orderUUID ? client?.orders?.getOrderAssembled(orderUUID) : null
    if (order == null) {
      if (_orderObj) {
        order = { ..._orderObj.order, assembledOrderItems: _orderObj.assembledOrderItems }
      } else {
        return null
      }
    }
    let {
      printLocationName = true,
      printLocationAddress = true,
      printDeviceName = true,
      printSysUserName = true,
      printItems = true,
      printOrderNumber = true,
      printOrderIdentifier = true,
      printOrderTimestamp = true,
      printNotes = true,
      printQRCode = true,
      printDiscountSummary = true,
      printNonFiscalDisclaimer = false,
      printExtendedTrackingInfo = false,
      maxChars = 48,
      headerPrintSlip = 0,
      footerPrintSlip = 0,
      ccPrintSlip = null,
      figletPrintSlip = null,
      extraPrintSlip = null,
      languageCode = 'hu'
    } = options || {}
    headerPrintSlip = (headerPrintSlip === 0 ? {
      Rows: [
        { Bold: true, Msg: strCenter('ÖSSZESÍTŐ', maxChars) }
      ]
    } : headerPrintSlip)
    footerPrintSlip = (footerPrintSlip === 0 ? {
      Rows: [
        { Bold: true, Msg: strCenter('Köszönjük a vásárlást!', maxChars) }
      ]
    } : footerPrintSlip)
    let {
      assembledOrderItems,
      orderNumber = '',
      orderIdentifier = '',
      notes,
      tipAmount,
      tippType = 0,
      discountPrice,
      payments,
      paymentRounding,
      insertTimestamp,
      totalPrice,
      discounts,
      discountAmount,
      requestedReadyTime,
      takeAway,
      deliveryCost,
      deliveryInfo,
      sumDiscountServiceFee,
      sumServiceFee,
      statuses = [],
      tableInfo,
      sandbox,
      orderType
    } = order
    let orderShortID = null
    let orderShortIDFormatted = null
    try {
      for (let status of statuses || []) {
        if (status.statusID === OrderStatus.ORDER_SHORTID_INSERTED && status?.notes?.shortID != null) {
          orderShortID = status?.notes?.shortID
          const orderShortIDSplit = orderShortID.match(/.{1,4}/g) ?? [];
          orderShortIDFormatted = orderShortIDSplit.join('-')
        }
      }
    } catch (err) {
      console.log('createPrintSlip', err)
    }
    let printSlip = { Rows: [] }
    if (printLocationName) {
      let row = { Bold: true, Font: 'double', Msg: strCenter(`${location.name}`, Math.round(maxChars / 2)) }
      printSlip.Rows.push(row)
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, ' ') })
    }
    if (printLocationAddress) {
      printSlip.Rows = printSlip.Rows.concat(formatLocationAddressPrintSlip(maxChars))
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, ' ') })
    }
    printSlip.Rows = printSlip.Rows.concat(headerPrintSlip ? headerPrintSlip.Rows : [])

    if (sandbox) printSlip.Rows.push({ Bold: true, Msg: strCenter('!!! TESZT RENDELÉS !!!', maxChars) })
    if (orderType === 1) printSlip.Rows.push({ Bold: true, Msg: strCenter('!!! SZEMÉLYZETI FOGYASZTÁS !!!', maxChars) })

    if (printDeviceName) {
      let row = { Bold: true, Msg: strLeftRight(`Eszköz:`, `${deviceName}`, maxChars) }
      printSlip.Rows.push(row)
    }

    if (printSysUserName && loggedInUser != null) {
      let row = { Bold: true, Msg: strLeftRight(`Felhasználó:`, `${loggedInUser?.firstName}`, maxChars) }
      printSlip.Rows.push(row)
    }

    if (printOrderNumber && orderNumber != null) {
      let row = { Bold: true, Msg: strLeftRight(`Rendelés:`, `${orderNumber}`, maxChars) }
      printSlip.Rows.push(row)
    }
    if (printOrderIdentifier && orderIdentifier != null && (orderIdentifier + '').length > 0) {
      let row = { Bold: true, Msg: strLeftRight(`ID:`, `${orderIdentifier}`, maxChars) }
      printSlip.Rows.push(row)
    }
    printSlip.Rows.push({ Bold: true, Msg: '' })
    if (printOrderTimestamp && insertTimestamp != null) {
      let row = { Bold: false, Msg: strLeftRight(`Rendelés ideje:`, `${moment(insertTimestamp).format('YY-MM-DD HH:mm')}`, maxChars) }
      printSlip.Rows.push(row)
    }
    if (requestedReadyTime != null) {
      let row = { Bold: false, Msg: strLeftRight(`Kért időpont:`, `${moment(requestedReadyTime).format('YY-MM-DD HH:mm')}`, maxChars) }
      printSlip.Rows.push(row)
    }

    if (tableInfo != null) {
      let message = strLeftRight(`Asztal:`, `${tableInfo.name}`, maxChars)
      let row = { Bold: true, Msg: message }
      printSlip.Rows.push(row)
    }
    if (deliveryInfo != null) {
      let rows = addressPrintSlip(deliveryInfo, options, true)
      printSlip.Rows = printSlip.Rows.concat(rows)
    }

    // Print figlet slip - It can be the ordernumber in ascii mode for example.
    if (figletPrintSlip) {
      try {
        printSlip.Rows = printSlip.Rows.concat(figletPrintSlip.Rows)
      } catch (error) {
        console.log('figletPrintSlip error', error)
      }
    }
    if (printItems) {
      try {
        let vatSums = {}
        let orderServiceFees = {}
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        for (let item of Object.values(assembledOrderItems || {})) {
          if (item.count > 0) {
            vatSums[item.vat] = (vatSums[item.vat] || 0) + item.sumDiscountPrice
            vatSums[`service ${item.vat}`] = (vatSums[`service ${item.vat}`] || 0) + item.serviceFee
            orderServiceFees[item.serviceFeePercent] = (orderServiceFees[item.serviceFeePercent] || 0) + item.serviceFee
            let itemName = `${getPreferredLanguage(item?.name, languageCode, 'Termék')}${((item.takeAway == null ? takeAway : item.takeAway) ? ' (TA)' : '')} ${getPreferredLanguage(item.sizeName, languageCode, '')}`
            let scaledItem = (item?.sizeAttributes?.scale && ['kg', 'liter'].includes(item.unitType))
            if (item.count > 1 || scaledItem || itemName.length > (maxChars - 12)) {
              // If the count is greater than 1, print the extra line with the unit multiplier
              printSlip.Rows.push({
                Bold: false,
                Msg: itemName
              })
              if (scaledItem) {
                printSlip.Rows.push({
                  Bold: false,
                  Msg: strLeftRight(
                    ' ' + (((item?.count * +item.quantity)?.toFixed(unitTypeFixed[item.unitType] || 0)) +
                      sizeUnitNames[item.unitType] || 'db').padStart(2, ' ') + ' x ' +
                    ` ${item.basePriceUnit}Ft/${(+item.quantityBase || +item.quantity)?.toFixed(unitTypeFixed[item.unitType] || 0)} ${sizeUnitNames[item.unitType]}`,
                    `${item.sumPrice}Ft ${vats[item.vat]}00`, maxChars)
                })
              } else {
                printSlip.Rows.push({
                  Bold: false,
                  Msg: strLeftRight(
                    ('' + item.count).padStart(2, ' ') + ' x ' +
                    ` ${item.basePriceUnit}Ft/${(+item.quantityBase)?.toFixed(unitTypeFixed[item.unitType] || 0)} ${sizeUnitNames[item.unitType]}`,
                    `${item.sumPrice}Ft ${vats[item.vat] || 'C'}00`, maxChars)
                })
              }
            } else {
              printSlip.Rows.push({
                Bold: false,
                Msg: strLeftRight(itemName, `${item.sumPrice}Ft ${vats[item.vat]}00`, maxChars)
              })
            }
            // Print modifiers
            if (item?.modifierGroups != null) {
              for (let modGroup of item?.modifierGroups) {
                for (let modifier of (modGroup?.modifiers || [])) {
                  if (modifier.count > 0) {
                    printSlip.Rows.push({ Bold: false, Msg: (modifier?.count + '').padStart(6, ' ') + ' x ' + getPreferredLanguage(modifier?.name, languageCode, 'Mod') + (modifier?.price > 0 ? ' (' + +modifier?.price + 'Ft)' : '') })
                  }
                }
              }
            }
            // Print total item discount
            if ((item.sumPriceUnit - item.sumDiscountPriceUnit) > 0) {
              printSlip.Rows.push({
                Bold: false,
                Msg: strLeftRight(`Kedvezmény`, `-${item.sumPrice - item.sumDiscountPrice}Ft ${vats[item.vat]}00`, maxChars)
              })
            }
            if ((item.sumPriceUnit - item.sumDiscountPriceUnit) < 0) {
              printSlip.Rows.push({
                Bold: false,
                Msg: strLeftRight(`Felár`, `${Math.abs(item.sumPrice - item.sumDiscountPrice)}Ft ${vats[item.vat]}00`, maxChars)
              })
            }
            // Print item packaging
            if (item.packagePrice > 0) {
              vatSums['27.00'] = (vatSums['27.00'] || 0) + (item.packageDiscountPrice * item.count)
              let packageName = getPreferredLanguage(item.package?.name, languageCode, 'Csomagolás')
              if (item.count > 1) {
                // If the count is greater than 1, print the extra line with the unit multiplier
                printSlip.Rows.push({
                  Bold: false,
                  Msg: packageName
                })
                printSlip.Rows.push({
                  Bold: false,
                  Msg: strLeftRight(
                    ' ' + (item?.count + '').padStart(2, ' ') + ' x ' + ` ${item.packagePrice}Ft/1 ${sizeUnitNames['piece']}`,
                    `${item.packagePrice * item.count}Ft ${vats['27.00']}00`, maxChars)
                })
              } else {
                printSlip.Rows.push({
                  Bold: false,
                  Msg: strLeftRight(packageName, `${item.packagePrice}Ft ${vats['27.00']}00`, maxChars)
                })
              }
              // Print total package discount
              if ((item.packagePrice - item.packageDiscountPrice) > 0) {
                printSlip.Rows.push({
                  Bold: false,
                  Msg: strLeftRight(`Kedvezmény`, `-${(item.packagePrice - item.packageDiscountPrice) * item.count}Ft ${vats['27.00']}00`, maxChars)
                })
              }
              if ((item.packagePrice - item.packageDiscountPrice) < 0) {
                printSlip.Rows.push({
                  Bold: false,
                  Msg: strLeftRight(`Felár`, `${Math.abs(item.packagePrice - item.packageDiscountPrice) * item.count}Ft ${vats['27.00']}00`, maxChars)
                })
              }
            }
            if (item.depositPrice > 0) {
              let depositVat = (+item.depositVat || 0).toFixed(2)
              vatSums[depositVat] = (vatSums[depositVat] || 0) + (item.depositPrice * item.count)
              if (item.count > 1) {
                // If the count is greater than 1, print the extra line with the unit multiplier
                printSlip.Rows.push({
                  Bold: false,
                  Msg: 'Betétdíj'
                })
                printSlip.Rows.push({
                  Bold: false,
                  Msg: strLeftRight(
                    ' ' + (item?.count + '').padStart(2, ' ') + ' x ' + ` ${+item.depositPrice}Ft/1 ${sizeUnitNames['piece']}`,
                    `${item.depositPrice * item.count}Ft ${vats[depositVat]}00`, maxChars)
                })
              } else {
                printSlip.Rows.push({
                  Bold: false,
                  Msg: strLeftRight('Betétdíj', `${+item.depositPrice}Ft ${vats[depositVat]}00`, maxChars)
                })
              }
            }
          }
        }
        for (let [key, value] of Object.entries(orderServiceFees || {})) {
          if (+value > 0) {
            printSlip.Rows.push({
              Bold: false,
              Msg: strLeftRight(`Szervizdíj ${key}%`, (`${value}Ft 000`), maxChars)
            })
          }
        }
        // Print for wine (borravaló)
        if (tipAmount > 0) {
          if (tippType === 1) {
            vatSums[`tip 0.00`] = (vatSums['tip 0.00'] || 0) + tipAmount
            printSlip.Rows.push({
              Bold: false,
              Msg: strLeftRight(`Borravaló`, (tipAmount + `Ft ${vats['0.00']}00`), maxChars)
            })
          } else {
            printSlip.Rows.push({
              Bold: false,
              Msg: strLeftRight(`Borravaló`, (tipAmount + `Ft    `), maxChars)
            })
          }
        }
        // Print delivery fee
        if (deliveryCost > 0) {
          vatSums['delivery 27.00'] = (vatSums['delivery 27.00'] || 0) + deliveryCost
          printSlip.Rows.push({
            Bold: false,
            Msg: strLeftRight(deliveryInfo?.feeName || `Elviteli díj`, (deliveryCost + `Ft ${vats['27.00']}00`), maxChars)
          })
        }
        // Print total
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows.push({ Bold: true, Msg: strLeftRight(`Összesítve:`, (discountPrice + 'Ft'), maxChars) })
        // Print payments
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        let noPayments = true
        for (let payment of payments || []) {
          if (!payment.deleted && payment.stornoPaymentUUID == null) {
            noPayments = false
            printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`${paymentBlockNames[payment.agentType]}:`, payment.amount + 'Ft', maxChars) })
          }
        }
        if (noPayments) {
          printSlip.Rows.push({ Bold: true, Msg: 'Nincs fizetve / Unpaid' })
        }
        // Print cash rounding (kerekítés)
        if (paymentRounding) {
          paymentRounding = (+paymentRounding).toFixed(2)
          printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`Kerekítés:`, (paymentRounding + 'Ft'), maxChars) })
        }
        // Áfa összegzése
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows.push({ Bold: true, Msg: `Áfa bontás:` })
        for (let [key, value] of Object.entries(vatSums || {})) {
          printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`${key}%`, (value + 'Ft'), maxChars) })
        }
      } catch (err) {
        console.log(err)
      }
    }
    // Kedvezmények összegzése
    if (printDiscountSummary && discountAmount !== 0) {
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`Eredeti ár:`, `${totalPrice}Ft`, maxChars) })
      if (discountAmount > 0) printSlip.Rows.push({ Bold: false, Msg: strLeftRight('Kedvezmény össz:', `-${discountAmount}Ft`, maxChars) })
      else printSlip.Rows.push({ Bold: false, Msg: strLeftRight('Felár össz:', `${Math.abs(discountAmount)}Ft`, maxChars) })
      //printSlip.Rows.push({ Bold: false, Msg: strLeftRight('Kedvezmények össz:', `-${discountAmount}Ft`, maxChars) })
      if ((sumServiceFee - sumDiscountServiceFee) !== 0) {
        printSlip.Rows.push({ Bold: false, Msg: strLeftRight('Szervizdíj korrekció:', `${-(sumServiceFee - sumDiscountServiceFee)}Ft`, maxChars) })
      }
      if (discountAmount > 0) printSlip.Rows.push({ Bold: true, Msg: strLeftRight('Kedvezményes ár:', `${discountPrice}Ft`, maxChars) })
      else printSlip.Rows.push({ Bold: true, Msg: strLeftRight('Feláras ár:', `${discountPrice}Ft`, maxChars) })
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      printSlip.Rows.push({ Bold: true, Msg: 'Kedvezmények/Felárak:' })
      for (let dis of discounts || []) {
        if (dis.amount > 0) {
          printSlip.Rows.push({
            Bold: false,
            Msg: strLeftRight(`${getPreferredLanguage(dis?.name, languageCode, 'Kedv./Felár')}`, `${+(dis?.amount || 0) + +(dis?.packageAmount || 0)}Ft`, maxChars)
          })
        }
      }
    }
    // Megjegyzés
    if (printNotes && notes != null && notes.length > 0) {
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      let noteLines = wordWrap(`${notes}`, maxChars)
      for (let line of noteLines) {
        let row = { Bold: false, Msg: line, Font: 'normal' }
        printSlip.Rows.push(row)
      }
    }
    // Credit card print slip print if exists
    if (ccPrintSlip) {
      try {
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows = printSlip.Rows.concat(ccPrintSlip.Rows)
      } catch (e) {
        console.log('ccPrintSlip', e)
      }
    }
    // Extra info if needed for example, a promotion
    if (extraPrintSlip) {
      try {
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows = printSlip.Rows.concat(extraPrintSlip.Rows)
      } catch (e) {
        console.log('extraPrintSlip', e)
      }
    }
    // Footer printslip, example: Thank you for shopping at xxx
    if (footerPrintSlip) {
      try {
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows = printSlip.Rows.concat(footerPrintSlip.Rows)
      } catch (e) {
        console.log('footerPrintSlip', e)
      }
    }
    // Needed when the order is printed with a non fiscal printer (simple pos printer)
    if (printNonFiscalDisclaimer) {
      try {
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows.push({ Bold: true, Msg: strCenter('Készpénz átvételére nem jogosít', maxChars) })
      } catch (e) {
        console.log('footerPrintSlip', e)
      }
    }
    // Order state tracking qr code
    if (printExtendedTrackingInfo) {
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      printSlip.Rows.push({ Bold: true, Msg: 'Rendelésed itt található:' })
      printSlip.Rows.push({ Bold: true, Msg: strCenter('https://barsoft.me', maxChars) })
      printSlip.Rows.push({ Bold: true, Msg: 'Azonosító kód:' })
      printSlip.Rows.push({ Bold: true, Msg: strCenter((orderShortIDFormatted?.length > 0 ? orderShortIDFormatted : orderUUID), maxChars) })
    } else {
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      printSlip.Rows.push({ Bold: true, Msg: strCenter('https://barsoft.me/' + (orderShortIDFormatted?.length > 0 ? orderShortID : ''), maxChars) })
    }
    if (printQRCode) {
      printSlip.Rows.push({ Position: 'center', Type: 'qrcode', Msg: 'https://barsoft.me/' + (orderShortIDFormatted?.length > 0 ? orderShortID : orderUUID) })
    }
    return printSlip
  }

  const createOrderPrintSlip = (orderUUID, options = {}, prepCounters = null, storno = false, _orderObj = null, attributes = {}) => {
    let order = client?.orders?.getOrderAssembled(orderUUID)
    if (order == null) {
      if (_orderObj) {
        order = { ..._orderObj.order, assembledOrderItems: _orderObj.assembledOrderItems }
      } else {
        return null
      }
    }
    const {
      languageCode = 'hu'
    } = attributes
    const {
      maxChars = 48
    } = options
    let {
      assembledOrderItems,
      takeAway = false,
      orderNumber = '',
      orderIdentifier = '',
      notes, tableInfo,
      insertDeviceType,
      requestedReadyTime,
      insertTimestamp,
      deliveryInfo,
      sandbox,
      orderType
    } = order
    let printSlip = { Rows: [] }
    if (assembledOrderItems != null) {
      if (orderType === 1) printSlip.Rows.push({ Bold: true, Msg: strCenter('!!! SZEMÉLYZETI FOGYASZTÁS !!!', maxChars) })
      if (sandbox) printSlip.Rows.push({ Bold: true, Msg: strCenter('!!! TESZT RENDELÉS !!!', maxChars) })
      if (insertDeviceType >= 1000) {
        printSlip.Rows.push({ Bold: true, Msg: `%%% ${deviceTypeName[insertDeviceType] || 'Online'} rendelés! %%%`, Font: 'double' })
      }
      if (storno) {
        printSlip.Rows.push({ Bold: true, Msg: '%%% TÖRÖLVE! %%%', Font: 'double' })
      }
      if (tableInfo != null) {
        let messageLeft = `Asztal: ${tableInfo.name}`
        let row = { Bold: true, Msg: messageLeft, Font: 'doubleHeight' }
        printSlip.Rows.push(row)
      }
      if (deliveryInfo != null) {
        let rows = addressPrintSlip(deliveryInfo, options)
        printSlip.Rows = printSlip.Rows.concat(rows)
      }
      if (orderNumber != null) {
        let messageLeft = `Rendelés: ${orderNumber}`
        let row = { Bold: true, Msg: messageLeft, Font: 'doubleHeight' }
        printSlip.Rows.push(row)
      }
      if (orderIdentifier != null && (orderIdentifier + '').length > 0) {
        let messageLeft = `ID: ${orderIdentifier}`
        let row = { Bold: true, Msg: messageLeft, Font: 'double' }
        printSlip.Rows.push(row)
      }
      if (insertDeviceType != null) {
        let messageLeft = `Eszköz: ${deviceTypeName[insertDeviceType] || 'N/A'}`
        let row = { Bold: true, Msg: messageLeft, Font: 'normal' }
        printSlip.Rows.push(row)
      }
      if (insertTimestamp != null) {
        let messageLeft = `Rendelés ideje: `
        let row = { Bold: false, Msg: messageLeft + `${moment(insertTimestamp).format('YY-MM-DD HH:mm')}`.padStart(maxChars - messageLeft.length, ' '), Font: 'doubleHeight' }
        printSlip.Rows.push(row)
      }
      if (requestedReadyTime != null) {
        let messageLeft = `Kért időpont: `
        let row = { Bold: true, Msg: messageLeft + `${moment(requestedReadyTime).format('YY-MM-DD HH:mm')}`.padStart(maxChars - messageLeft.length, ' '), Font: 'doubleHeight' }
        printSlip.Rows.push(row)
      }
      if (notes != null && notes.length > 0) {
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        let noteLines = wordWrap(`${notes}`, maxChars)
        for (let line of noteLines) {
          let row = { Bold: false, Msg: line, Font: 'normal' }
          printSlip.Rows.push(row)
        }
      }
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      let count = 0
      let currentServingID = 0
      let items = Object.values(cloneDeep(assembledOrderItems || {}))
      let sortedItems = items.sort((a, b) => (a?.insertTimestamp || 0) - (b?.insertTimestamp || 0)).sort((a, b) => (a?.servingID || 0) - (b?.servingID || 0))
      let menuGroups = {}
      let menuIdx = 65
      for (let item of sortedItems) {
        if (item.sizeAttributes?.menuSize) {
          menuGroups[item.orderItemUUID] = { menuID: String.fromCharCode(menuIdx) }
          menuIdx++
          //console.log(menuGroups)
        }
      }
      for (let item of sortedItems) {
        if (item.count > 0) {
          // Check if we need to filter items
          //console.log(item.prep, prepCounters)
          let match = (Object.values(item.prep).length === 0 || prepCounters == null || prepCounters.length === 0)
          if (!match) {
            for (let prepCounter of Object.values(item.prep || {})) {
              if (prepCounters.includes(prepCounter.id)) {
                match = true
              }
            }
          }
          if (!match) continue;
          count++
          if (!storno && (item.servingID || 0) !== currentServingID) {
            currentServingID = item.servingID || 0
            printSlip.Rows.push({ Msg: '' })
            let servingTxt = ` ${currentServingID}. KÖR `
            let charsLeft = Math.round(maxChars / 2) - Math.round(servingTxt.length / 2)
            printSlip.Rows.push({
              Bold: true,
              Msg: servingTxt.padStart(charsLeft, '-') + ''.padEnd(maxChars - charsLeft - servingTxt.length, '-'),
              Font: 'double'
            })
            printSlip.Rows.push({ Msg: '' })
          }
          if (storno) {
            printSlip.Rows.push({ Bold: true, Msg: '%%% TÖRÖLVE! %%%', Font: 'double' })
          }
          let row = { Bold: true, Msg: '', Font: 'doubleHeight' }
          let scaledItem = (item?.sizeAttributes?.scale && ['kg', 'liter'].includes(item.unitType))
          let itemLangName = getPreferredLanguage(item.name, languageCode, 'Termék')
          let sizeLangName = getPreferredLanguage(item.sizeName, languageCode, '')
          let itemName = attributes?.orderSlip?.sizeBeforeName ? `${sizeLangName} ${itemLangName}` : `${itemLangName} ${sizeLangName}`
          //console.log(item.sizeAttributes)
          if (item.sizeAttributes?.menuSize) {
            itemName = `-${menuGroups[item.orderItemUUID].menuID}- ${itemName}`
          } else {
            if (item.parentOrderItemUUID && menuGroups[item.parentOrderItemUUID]) {
              itemName = `-${menuGroups[item.parentOrderItemUUID].menuID}- ${itemName}`
            }
          }
          row.Msg = (scaledItem ? (+item.quantity * item.count).toFixed(3) + sizeUnitNames[item.unitType] : (item?.count + '').padStart(2, ' ')) + ' x ' + itemName
          printSlip.Rows.push(row)
          if (item.takeAway == null ? takeAway : item.takeAway) {
            let row2 = { Bold: true, Msg: '', Font: 'doubleHeight' }
            row2.Msg = (''.padStart(6, ' ') + '### ELVITELRE ###').padStart(6, ' ')
            printSlip.Rows.push(row2)
          }
          // Print modifiers
          if (item?.modifierGroups != null) {
            for (let modGroup of item?.modifierGroups) {
              for (let modifier of (modGroup?.modifiers || [])) {
                if (modifier.count > 0) {
                  let row = { Bold: false, Msg: '', Font: 'doubleHeight' }
                  let modName = getPreferredLanguage(modifier?.name, languageCode, 'Mod')
                  row.Msg = (modifier?.count > 1 ? ((modifier?.count + '').padStart(6, ' ') + 'x ') : ''.padStart(6, ' ')) + modName
                  printSlip.Rows.push(row)
                }
              }
            }
          }
          if (item.notes != null && item.notes.length > 0) {
            let noteLines = wordWrap(`${item.notes}`, maxChars - 4)
            for (let line of noteLines) {
              let row = { Bold: false, Msg: strRight('# ' + line, maxChars), Font: 'normal' }
              printSlip.Rows.push(row)
            }
          }
        }
      }
      if (count === 0) return { Rows: [] }
    }
    return printSlip
  }

  const createOrderItemPrintSlip = (orderUUID, orderItemUUID = null, options = {}, prepCounters = null, storno = false, _orderObj = null, attributes = {}) => {
    let order = client?.orders?.getOrderAssembled(orderUUID)
    //console.log({order})
    if (order == null) {
      if (_orderObj) {
        order = { ..._orderObj.order, assembledOrderItems: _orderObj.assembledOrderItems }
      } else {
        return null
      }
    }
    const {
      languageCode = 'hu'
    } = attributes
    const {
      maxChars = 48
    } = options
    let {
      assembledOrderItems,
      takeAway = false,
      orderNumber = '',
      orderIdentifier = '',
      tableInfo,
      insertDeviceType,
      insertTimestamp,
      sandbox
    } = order
    let printSlip = { Rows: [] }
    if (assembledOrderItems != null) {
      if (sandbox) {
        printSlip.Rows.push({ Bold: true, Msg: strCenter('!!! TESZT RENDELÉS !!!', maxChars) })
      }
      if (storno) {
        printSlip.Rows.push({ Bold: true, Msg: '%%% TÖRÖLVE! %%%', Font: 'double' })
      }
      if (tableInfo != null) {
        let messageLeft = `Asztal: ${tableInfo.name}`
        let row = { Bold: true, Msg: messageLeft, Font: 'doubleHeight' }
        printSlip.Rows.push(row)
      }
      if (orderNumber != null) {
        let messageLeft = `Rendelés: ${orderNumber}`
        let row = { Bold: true, Msg: messageLeft, Font: 'doubleHeight' }
        printSlip.Rows.push(row)
      }
      if (orderIdentifier != null && (orderIdentifier + '').length > 0) {
        let messageLeft = `ID: ${orderIdentifier}`
        let row = { Bold: true, Msg: messageLeft, Font: 'double' }
        printSlip.Rows.push(row)
      }
      if (insertDeviceType != null) {
        let messageLeft = `Eszköz: ${deviceTypeName[insertDeviceType] || 'N/A'}`
        let row = { Bold: true, Msg: messageLeft, Font: 'normal' }
        printSlip.Rows.push(row)
      }
      if (insertTimestamp != null) {
        let messageLeft = `Rendelés ideje: `
        let row = { Bold: false, Msg: messageLeft + `${moment(insertTimestamp).format('YY-MM-DD HH:mm')}`.padStart(maxChars - messageLeft.length, ' '), Font: 'doubleHeight' }
        printSlip.Rows.push(row)
      }
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      let count = 0
      let currentServingID = 0
      for (let item of Object.values(assembledOrderItems || {}).sort((a, b) => (a?.servingID || 0) - (b?.servingID || 0))) {
        if (item.count > 0 && orderItemUUID === item.orderItemUUID) {
          // Check if we need to filter items
          //console.log(item.prep, prepCounters)
          let match = (Object.values(item.prep).length === 0 || prepCounters == null || prepCounters.length === 0)
          if (!match) {
            for (let prepCounter of Object.values(item.prep || {})) {
              if (prepCounters.includes(prepCounter.id)) {
                match = true
              }
            }
          }
          if (!match) continue;
          count++
          if (!storno && (item.servingID || 0) !== currentServingID) {
            currentServingID = item.servingID || 0
            printSlip.Rows.push({ Msg: '' })
            let servingTxt = ` ${currentServingID}. KÖR `
            let charsLeft = Math.round(maxChars / 2) - Math.round(servingTxt.length / 2)
            printSlip.Rows.push({
              Bold: true,
              Msg: servingTxt.padStart(charsLeft, '-') + ''.padEnd(maxChars - charsLeft - servingTxt.length, '-'),
              Font: 'double'
            })
            printSlip.Rows.push({ Msg: '' })
          }
          if (storno) {
            printSlip.Rows.push({ Bold: true, Msg: '%%% TÖRÖLVE! %%%', Font: 'double' })
          }
          let row = { Bold: true, Msg: '', Font: 'doubleHeight' }
          let scaledItem = (item?.sizeAttributes?.scale && ['kg', 'liter'].includes(item.unitType))
          let itemLangName = getPreferredLanguage(item.name, languageCode, 'Termék')
          let sizeLangName = getPreferredLanguage(item.sizeName, languageCode, '')
          let itemName = attributes?.orderSlip?.sizeBeforeName ? `${sizeLangName} ${itemLangName}` : `${itemLangName} ${sizeLangName}`
          row.Msg = (scaledItem ? (+item.quantity * item.count).toFixed(3) + sizeUnitNames[item.unitType] : (item?.count + '').padStart(2, ' ')) + ' x ' + itemName
          printSlip.Rows.push(row)
          if (item.takeAway == null ? takeAway : item.takeAway) {
            let row2 = { Bold: true, Msg: '', Font: 'doubleHeight' }
            row2.Msg = (''.padStart(6, ' ') + '### ELVITELRE ###').padStart(6, ' ')
            printSlip.Rows.push(row2)
          }
          // Print modifiers
          if (item?.modifierGroups != null) {
            for (let modGroup of item?.modifierGroups) {
              for (let modifier of (modGroup?.modifiers || [])) {
                if (modifier.count > 0) {
                  let row = { Bold: false, Msg: '', Font: 'doubleHeight' }
                  let modName = getPreferredLanguage(modifier?.name, languageCode, 'Mod')
                  row.Msg = (modifier?.count > 1 ? ((modifier?.count + '').padStart(6, ' ') + 'x ') : ''.padStart(6, ' ')) + modName
                  printSlip.Rows.push(row)
                }
              }
            }
          }
          if (item.notes != null && item.notes.length > 0) {
            let noteLines = wordWrap(`${item.notes}`, maxChars - 4)
            for (let line of noteLines) {
              let row = { Bold: false, Msg: strRight('# ' + line, maxChars), Font: 'normal' }
              printSlip.Rows.push(row)
            }
          }
        }
      }
      if (count === 0) return { Rows: [] }
    }
    return printSlip
  }

  const printOrderSlips = async (order) => {
    try {
      let printError = []
      for await (let printer of Object.values(printersRef.current || {})) {
        //console.log({printer})
        if (printer.printOrder) {
          //console.log(order)
          let printSlip = createOrderPrintSlip(order?.order?.uuid, { maxChars: printer.maxChars || 48 }, printer.prepCounters, false, null, printer.attributes)
          if (printSlip?.Rows?.length > 0) {
            let res = await posPrint(printSlip, printer.uuid)
            if (!res.success) {
              printError.push({ ...printer, result: res })
            }
          }
        }
      }
      order.statuses.addStatus(OrderStatus.ORDER_PRINT_ONLINE_ORDER_SLIPS_RESULT, cloneDeep(printError))
      if (printError.length > 0) {
        return okeResponse(false, 206, 'Some errors', printError)
      } else {
        return okeResponse(true, 200, 'Success', null)
      }
    } catch (e) {
      console.error(e)
      return okeResponse(false, 500, 'Internal error', null)
    }
  }

  const printOrderStornoSlips = async (order) => {
    try {
      let printError = []
      for await (let printer of Object.values(printersRef.current || {})) {
        //console.log({printer})
        if (printer.printOrder) {
          //console.log(order)
          let printSlip = createOrderPrintSlip(order?.order?.uuid, { maxChars: printer.maxChars || 48 }, printer.prepCounters, true, null, printer.attributes)
          if (printSlip?.Rows?.length > 0) {
            let res = await posPrint(printSlip, printer.uuid)
            if (!res.success) {
              printError.push({ ...printer, result: res })
            }
          }
        }
      }
      if (printError.length > 0) {
        return okeResponse(false, 206, 'Some errors', printError)
      } else {
        return okeResponse(true, 200, 'Success', null)
      }
    } catch (e) {
      console.error(e)
      return okeResponse(false, 500, 'Internal error', null)
    }
  }

  const printOrderItemStornoSlips = async (orderUUID, orderItemUUID) => {
    try {
      let printError = []
      for await (let printer of Object.values(printersRef.current || {})) {
        if (printer.printOrder) {
          let printSlip = createOrderItemPrintSlip(orderUUID, orderItemUUID, { maxChars: printer.maxChars || 48 }, printer.prepCounters, true, printer.attributes)

          if (printSlip?.Rows?.length > 0) {
            let res = await posPrint(printSlip, printer.uuid)
            if (!res.success) {
              printError.push({ ...printer, result: res })
            }
          }
        }
      }
      if (printError.length > 0) {
        return okeResponse(false, 206, 'Some errors', printError)
      } else {
        return okeResponse(true, 200, 'Success', null)
      }
    } catch (e) {
      console.error(e)
      return okeResponse(false, 500, 'Internal error', null)
    }
  }

  const onPrintOrderSlip = (data) => {
    client?.orders.loadOrder(data.uuid).then(order => {
      printOrderSlips(order).then(res => {
        //console.log('Server requested print slips for: ', data.orderNumber, { result: res })
      })
    })
  }

  const onOrderExternalOrderNumberReceived = ({ orderUUID, externalOrderNumber }) => {
    if (deviceParams?.integrations?.fruitsys?.printOrderNumberSlip) {
      client?.orders.loadOrder(orderUUID).then(order => {
        //console.log('External order number received for: ', externalOrderNumber, order)
        if (deviceParams?.posPrinter?.selected) {
          let { maxChars = 32 } = printersRef.current?.[deviceParams?.posPrinter?.uuid] || {}
          let printSlip = createOrderExternalNumberPrintSlip(order.order, externalOrderNumber + '', { maxChars: maxChars })
          posPrint(printSlip, deviceParams?.posPrinter?.uuid)
        }
        if (typeof extOrderNumberFiscalPrintCB.current === 'function') {
          let { maxChars = 32 } = deviceParams?.printer || {}
          let printSlip = createOrderExternalNumberPrintSlip(order.order, externalOrderNumber + '', { maxChars: maxChars })
          extOrderNumberFiscalPrintCB.current(printSlip)
        }
      })
    }
  }

  const createExternalPaymentPrintSlip = (payment, options = {}) => {
    let { rounding = 0, amount = 0, insertTimestamp = null, tippType = 1, uuid: paymentUUID = null, agentType = 'bankCard', sandbox = false } = payment
    let { items, sums } = payment.summary || {}
    const paymentShortIDSplit = payment?.shortID?.match(/.{1,4}/g) ?? [];
    const paymentShortIDFormatted = paymentShortIDSplit.join('-')
    let {
      printLocationName = true,
      printLocationAddress = true,
      printDeviceName = true,
      printSysUserName = true,
      printItems = true,
      printOrderTimestamp = true,
      printDiscountSummary = true,
      printNonFiscalDisclaimer = false,
      maxChars = 48,
      headerPrintSlip = 0,
      footerPrintSlip = 0,
      ccPrintSlip = null,
      figletPrintSlip = null,
      extraPrintSlip = null,
      printQRCode = true,
      languageCode = 'hu'
    } = options || {}
    headerPrintSlip = (headerPrintSlip === 0 ? {
      Rows: [
        { Bold: true, Msg: strCenter(agentType ? 'ÖSSZESÍTŐ' : 'ÖSSZESÍTŐ (ELŐNÉZET)', maxChars) }
      ]
    } : headerPrintSlip)
    footerPrintSlip = (footerPrintSlip === 0 ? {
      Rows: [
        { Bold: true, Msg: strCenter('Köszönjük a vásárlást!', maxChars) }
      ]
    } : footerPrintSlip)
    let {
      tippAmount,
      deliveryCost,
      totalPrice,
      discountPrice,
      discountAmount
    } = sums

    let printSlip = { Rows: [] }

    if (printLocationName) {
      let row = { Bold: true, Font: 'double', Msg: strCenter(`${location.name}`, Math.round(maxChars / 2)) }
      printSlip.Rows.push(row)
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, ' ') })
    }
    if (printLocationAddress) {
      printSlip.Rows = printSlip.Rows.concat(formatLocationAddressPrintSlip(maxChars))
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, ' ') })
    }
    printSlip.Rows = printSlip.Rows.concat(headerPrintSlip ? headerPrintSlip.Rows : [])

    if (sandbox) {
      printSlip.Rows.push({ Bold: true, Msg: strCenter('!!! TESZT RENDELÉS !!!', maxChars) })
    }

    if (printDeviceName) {
      let row = { Bold: true, Msg: strLeftRight(`Eszköz:`, `${deviceName}`, maxChars) }
      printSlip.Rows.push(row)
    }

    if (printSysUserName && loggedInUser != null) {
      let row = { Bold: true, Msg: strLeftRight(`Felhasználó:`, `${loggedInUser?.firstName}`, maxChars) }
      printSlip.Rows.push(row)
    }

    printSlip.Rows.push({ Bold: true, Msg: '' })
    if (printOrderTimestamp && insertTimestamp != null) {
      let row = { Bold: false, Msg: strLeftRight(`Rendelés ideje:`, `${moment(insertTimestamp).format('YY-MM-DD HH:mm')}`, maxChars) }
      printSlip.Rows.push(row)
    }
    // Print figlet slip - It can be the ordernumber in ascii mode for example.
    if (figletPrintSlip) {
      printSlip.Rows = printSlip.Rows.concat(figletPrintSlip.Rows)
    }
    if (printItems) {
      let paymentServiceFees = {}
      let vatSums = {}
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      for (let item of items) {
        vatSums[item.vat] = (vatSums[item.vat] || 0) + item.sumDiscountPrice
        vatSums[`service ${item.vat}`] = (vatSums[`service ${item.vat}`] || 0) + item.serviceFee
        paymentServiceFees[item.serviceFeePercent] = (paymentServiceFees[item.serviceFeePercent] || 0) + item.serviceFee
        if (item.count > 0) {
          let size = item.size
          let itemName = item.itemName + ' ' + item.sizeName || ''
          let scaledItem = (item?.sizeAttributes?.scale && ['kg', 'liter'].includes(item.unitType))
          if (item.count > 1 || scaledItem || itemName.length > (maxChars - 12)) {
            // If the count is greater than 1, print the extra line with the unit multiplier
            printSlip.Rows.push({
              Bold: false,
              Msg: itemName
            })
            if (scaledItem) {
              printSlip.Rows.push({
                Bold: false,
                Msg: strLeftRight(
                  ' ' + (((+item?.count * +item.quantity)?.toFixed(unitTypeFixed[size?.unitType] || 0)) +
                    sizeUnitNames[size?.unitType] || 'db').padStart(2, ' ') + ' x ' +
                  ` ${item.basePriceUnit}Ft/${(+item.quantityBase || +item.quantity)?.toFixed(unitTypeFixed[size?.unitType] || 0)} ${sizeUnitNames[size?.unitType]}`,
                  `${item.sumPrice}Ft ${vats[item.vat]}00`, maxChars)
              })
            } else {
              printSlip.Rows.push({
                Bold: false,
                Msg: strLeftRight(
                  //' ' + (item?.count + '').padStart(2, ' ') + ' x ' + ` ${item.sumPriceUnit}Ft/${item.quantity} ${sizeUnitNames[size.unitType]}`,
                  ('' + item?.count).padStart(2, ' ') + ' x ' +
                  ` ${item.basePriceUnit}Ft/${(+item.quantityBase)?.toFixed(unitTypeFixed[size?.unitType] || 0)} ${sizeUnitNames[size?.unitType]}`,
                  `${item.sumPrice}Ft ${vats[item.vat]}00`, maxChars)
              })
            }
          } else {
            printSlip.Rows.push({
              Bold: false,
              Msg: strLeftRight(itemName, `${item.sumPrice}Ft ${vats[item.vat]}00`, maxChars)
            })
          }
          // Print modifiers
          if (item?.modifierGroups != null) {
            for (let modGroup of item?.modifierGroups) {
              for (let modifier of (modGroup?.modifiers || [])) {
                if (modifier.count > 0) {
                  let modName = getPreferredLanguage(modifier?.name, languageCode, 'Mod')
                  printSlip.Rows.push({ Bold: false, Msg: (modifier?.count + '').padStart(6, ' ') + ' x ' + modName + (modifier?.price > 0 ? ' (' + +modifier?.price + 'Ft)' : '') })
                }
              }
            }
          }
          // Print total item discount
          if ((item.sumPriceUnit - item.sumDiscountPriceUnit) > 0) {
            printSlip.Rows.push({
              Bold: false,
              Msg: strLeftRight(`Kedvezmény`, `-${item.sumPrice - item.sumDiscountPrice}Ft ${vats[item.vat]}00`, maxChars)
            })
          }
          if ((item.sumPriceUnit - item.sumDiscountPriceUnit) < 0) {
            printSlip.Rows.push({
              Bold: false,
              Msg: strLeftRight(`Felár`, `${Math.abs(item.sumPrice - item.sumDiscountPrice)}Ft ${vats[item.vat]}00`, maxChars)
            })
          }
          // Print item packaging
          if (item.packagePrice > 0) {
            vatSums['27.00'] = (vatSums['27.00'] || 0) + (item.packageDiscountPrice * item.count)
            let packageName = getPreferredLanguage(item.package?.name, languageCode, 'Csomagolás')
            if (item.count > 1) {
              // If the count is greater than 1, print the extra line with the unit multiplier
              printSlip.Rows.push({
                Bold: false,
                Msg: packageName
              })
              printSlip.Rows.push({
                Bold: false,
                Msg: strLeftRight(
                  ' ' + (item?.count + '').padStart(2, ' ') + ' x ' + ` ${item.packagePrice}Ft/1 ${sizeUnitNames['piece']}`,
                  `${item.packagePrice * item.count}Ft ${vats['27.00']}00`, maxChars)
              })
            } else {
              printSlip.Rows.push({
                Bold: false,
                Msg: strLeftRight(packageName, `${item.packagePrice}Ft ${vats['27.00']}00`, maxChars)
              })
            }
            // Print total package discount
            if ((item.packagePrice - item.packageDiscountPrice) > 0) {
              printSlip.Rows.push({
                Bold: false,
                Msg: strLeftRight(`Kedvezmény`, `-${(item.packagePrice - item.packageDiscountPrice) * item.count}Ft ${vats['27.00']}00`, maxChars)
              })
            }
            if ((item.packagePrice - item.packageDiscountPrice) < 0) {
              printSlip.Rows.push({
                Bold: false,
                Msg: strLeftRight(`Felár`, `${Math.abs(item.packagePrice - item.packageDiscountPrice) * item.count}Ft ${vats['27.00']}00`, maxChars)
              })
            }
          }
          if (item.depositPrice > 0) {
            let depositVat = (+item.depositVat || 0).toFixed(2)
            vatSums[depositVat] = (vatSums[depositVat] || 0) + (item.depositPrice * item.count)
            if (item.count > 1) {
              // If the count is greater than 1, print the extra line with the unit multiplier
              printSlip.Rows.push({
                Bold: false,
                Msg: 'Betétdíj'
              })
              printSlip.Rows.push({
                Bold: false,
                Msg: strLeftRight(
                  ' ' + (item?.count + '').padStart(2, ' ') + ' x ' + ` ${+item.depositPrice}Ft/1 ${sizeUnitNames['piece']}`,
                  `${item.depositPrice * item.count}Ft ${vats[depositVat]}00`, maxChars)
              })
            } else {
              printSlip.Rows.push({
                Bold: false,
                Msg: strLeftRight('Betétdíj', `${+item.depositPrice}Ft ${vats[depositVat]}00`, maxChars)
              })
            }
          }
        }
      }
      for (let [key, value] of Object.entries(paymentServiceFees || {})) {
        if (+value > 0) {
          printSlip.Rows.push({
            Bold: false,
            Msg: strLeftRight(`Szervizdíj ${key}%`, (`${value}Ft 000`), maxChars)
          })
        }
      }
      // Print for wine (borravaló)
      if (tippAmount > 0 && tippType === 1) {
        vatSums['tip 0.00'] = (vatSums['tip 0.00'] || 0) + tippAmount
        printSlip.Rows.push({
          Bold: false,
          Msg: strLeftRight(`Borravaló`, (tippAmount + `Ft ${vats['0.00']}00`), maxChars)
        })
      }
      // Print delivery fee
      if (deliveryCost > 0) {
        vatSums['delivery 27.00'] = (vatSums['delivery 27.00'] || 0) + deliveryCost
        printSlip.Rows.push({
          Bold: false,
          Msg: strLeftRight(`Szállítási díj`, (deliveryCost + `Ft ${vats['27.00']}00`), maxChars)
        })
      }
      // Print total
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      printSlip.Rows.push({ Bold: true, Msg: strLeftRight(`Összesen:`, (discountPrice + 'Ft'), maxChars) })
      // Print payments
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      if (agentType == null) {
        printSlip.Rows.push({ Bold: true, Msg: `NINCS FIZETVE!` })
      } else {
        printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`${paymentBlockNames[agentType]}:`, amount + 'Ft', maxChars) })
        if (+rounding !== 0) {
          rounding = (+rounding).toFixed(2)
          printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`Kerekítés:`, (rounding + 'Ft'), maxChars) })
        }
      }
      // Áfa összegzése
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      printSlip.Rows.push({ Bold: true, Msg: `Áfa bontás:` })
      for (let [key, value] of Object.entries(vatSums || {})) {
        printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`${key}%`, (value + 'Ft'), maxChars) })
      }
    }
    // Kedvezmények összegzése
    if (printDiscountSummary && +discountAmount !== 0) {
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`Eredeti ár:`, `${totalPrice}Ft`, maxChars) })
      if (+discountAmount > 0) printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`Kedvezmény össz:`, `-${+discountAmount}Ft`, maxChars) })
      else printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`Felár össz:`, `${Math.abs(+discountAmount)}Ft`, maxChars) })
      if ((+discountPrice - +totalPrice + +discountAmount) !== 0) {
        printSlip.Rows.push({ Bold: false, Msg: strLeftRight('Szervizdíj korrekció:', `${-(+discountPrice - +totalPrice + +discountAmount)}Ft`, maxChars) })
      }
      if (+discountAmount > 0) printSlip.Rows.push({ Bold: true, Msg: strLeftRight('Kedvezményes ár:', `${discountPrice}Ft`, maxChars) })
      else printSlip.Rows.push({ Bold: true, Msg: strLeftRight('Feláras ár:', `${discountPrice}Ft`, maxChars) })
    }
    // Credit card print slip print if exists
    if (ccPrintSlip) {
      try {
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows = printSlip.Rows.concat(ccPrintSlip.Rows)
      } catch (e) {
        console.log('ccPrintSlip', e)
      }
    }
    // Extra info if needed for example, a promotion
    if (extraPrintSlip) {
      try {
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows = printSlip.Rows.concat(extraPrintSlip.Rows)
      } catch (e) {
        console.log('extraPrintSlip', e)
      }
    }
    // Footer printslip, example: Thank you for shopping at xxx
    if (footerPrintSlip) {
      try {
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows = printSlip.Rows.concat(footerPrintSlip.Rows)
      } catch (e) {
        console.log('footerPrintSlip', e)
      }
    }
    // Needed when the order is printed with a non fiscal printer (simple pos printer)
    if (printNonFiscalDisclaimer) {
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      printSlip.Rows.push({ Bold: true, Msg: strCenter('Készpénz átvételére nem jogosít', maxChars) })
    }
    // Payment qr code
    printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
    printSlip.Rows.push({ Bold: true, Msg: 'Rendelésed itt található:' })
    printSlip.Rows.push({ Bold: true, Msg: strCenter('https://barsoft.me', maxChars) })
    printSlip.Rows.push({ Bold: true, Msg: 'Azonosító kód:' })
    printSlip.Rows.push({ Bold: true, Msg: strCenter((paymentShortIDFormatted?.length > 0 ? paymentShortIDFormatted : paymentUUID), maxChars) })
    if (printQRCode) {
      printSlip.Rows.push({ Position: 'center', Type: 'qrcode', Msg: 'https://barsoft.me/' + (paymentShortIDFormatted?.length > 0 ? payment?.shortID : paymentUUID) })
    }
    return printSlip
  }

  const createXReportPrintSlip = (sums, maxChars = 32) => {
    let printSlip = {
      Rows: []
    }
    // Header
    let row = { Bold: true, Font: 'double', Msg: strCenter(`${location.name}`, Math.round(maxChars / 2)) }
    printSlip.Rows.push(row)
    printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, ' ') })

    printSlip.Rows = printSlip.Rows.concat(formatLocationAddressPrintSlip(maxChars))
    printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, ' ') })

    let paymentAgents = {}
    for(let value of devicePaymentAgents || []) {
      paymentAgents[value.paymentAgentID] = value
    }

    // System info
    printSlip.Rows.push({ Bold: true, Msg: strCenter('Műszak összesítő', maxChars) })
    printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
    printSlip.Rows.push({ Bold: true, Msg: `Eszköz: ${deviceName}` })
    printSlip.Rows.push({ Bold: true, Msg: `Nyomtatás ideje: ${moment().format('YY-MM-DD HH:mm')}` })
    printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
    printSlip.Rows.push({ Bold: true, Msg: `Nyitás: ${moment(sums.openTime).format('YY-MM-DD HH:mm')}` })
    if (sums.closeTime) {
      printSlip.Rows.push({ Bold: true, Msg: `Zárás: ${moment(sums.closeTime).format('YY-MM-DD HH:mm')}` })
    } else {
      printSlip.Rows.push({ Bold: true, Msg: `Zárás: Műszak nyitva` })
    }
    printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
    // Print pénztár current totals
    printSlip.Rows.push({ Bold: true, Msg: 'Pénztár állapot' })
    for (let [key, value] of Object.entries(sums?.agentTypes || {})) {
      printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`${agentTypeNames[key]}`, `${value}Ft`, maxChars) })
    }
    printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })

    // Print pénztár áfa bontás - termék típusok
    printSlip.Rows.push({ Bold: true, Msg: 'Pénztár áfa bontás (típusok)' })
    for (let [itemType, value] of Object.entries(sums?.vatSumsByItemTypes || {})) {
      for (let [vatPercent, vatAmount] of Object.entries(value || {})) {
        printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`${saleItemTypes[itemType]} ${vatPercent}%`, `${vatAmount}Ft`, maxChars) })
      }
    }
    printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })

    // Print pénztár áfa bontás - sima
    printSlip.Rows.push({ Bold: true, Msg: 'Pénztár áfa bontás (össz.)' })
    for (let [vatPercent, vatAmount] of Object.entries(sums?.vatSums || {})) {
      printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`${vatPercent}%`, `${vatAmount}Ft`, maxChars) })
    }
    printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })

    // Print pénztár be
    printSlip.Rows.push({ Bold: true, Msg: 'Pénztár pénz/be ' })
    for (let [key, value] of Object.entries(sums?.payInByReason || {})) {
      printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`${payInTypeNames[key] || 'N/A'}`, `${value}Ft`, maxChars) })
    }
    printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })

    printSlip.Rows.push({ Bold: false, Msg: 'Pénztár pénz/be pénznemenként' })
    if (Object.entries(sums?.payInAgentTree || {}).length === 0) {
      printSlip.Rows.push({ Bold: false, Msg: '  Nincs pénz be' })
    }
    if (Object.entries(sums?.payInAgentTree || {}).length > 0) {
      for (let [key, value] of Object.entries(sums?.payInAgentTree || {})) {
        printSlip.Rows.push({ Bold: false, Msg: `${agentTypeNames[key]}` })
        for (let [subKey, subValue] of Object.entries(value || {})) {
          printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`  ${paymentAgents[subKey]?.name?.hu || agentTypeNames[key]}`, `${subValue}Ft`, maxChars) })
        }
      }
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
    }
    // Print pénztár ki
    printSlip.Rows.push({ Bold: true, Msg: 'Pénztár pénz/ki' })
    if (Object.entries(sums?.payOutByReason || {}).length === 0) {
      printSlip.Rows.push({ Bold: false, Msg: '  Nincs pénz ki' })
    }
    for (let [key, value] of Object.entries(sums?.payOutByReason || {})) {
      printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`${payOutTypeNames[key] || 'N/A'}`, `${value}Ft`, maxChars) })
    }
    printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })

    if (Object.entries(sums?.payOutAgentTree || {}).length > 0) {
      printSlip.Rows.push({ Bold: false, Msg: 'Pénztár pénz/ki pénznemenként' })
      for (let [key, value] of Object.entries(sums?.payOutAgentTree || {})) {
        printSlip.Rows.push({ Bold: false, Msg: `${agentTypeNames[key]}` })
        for (let [subKey, subValue] of Object.entries(value || {})) {
          printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`  ${paymentAgents[subKey]?.name?.hu || agentTypeNames[key]}`, `${subValue}Ft`, maxChars) })
        }
      }
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
    }

    // Sale item sums
    printSlip.Rows.push({ Bold: true, Msg: 'Termék eladás (típus)' })
    let itemSum = 0
    for (let [key, value] of Object.entries(sums?.itemTypeSums || {})) {
      printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`${saleItemTypes[key] || 'N/A'}`, `${value}Ft`, maxChars) })
      itemSum += +value
    }
    printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`Összesen:`, `${itemSum}Ft`, maxChars) })
    if (sums?.receiptCount) {
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`Eladás nyugták össz:`, `${sums?.receiptCount}`, maxChars) })
      // Sale item sums
      printSlip.Rows.push({ Bold: true, Msg: 'Termék eladás (név)' })
      for (let [key, value] of Object.entries(sums?.itemCounts || {})) {
        printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`${value} x ${key}`, `${sums.itemSums[key]}Ft`, maxChars) })
      }
    }
    if (sums?.stornoCount) {
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
      printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`Sztorno nyugták össz:`, `${sums?.stornoCount}`, maxChars) })
      // Storno item sums
      printSlip.Rows.push({ Bold: true, Msg: 'Termék sztornó (név)' })
      for (let [key, value] of Object.entries(sums?.itemStornoCounts || {})) {
        printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`${value} x ${key}`, `${sums.itemStornoSums[key]}Ft`, maxChars) })
      }
    }

    printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
    printSlip.Rows.push({ Bold: false, Msg: 'Eladás pénznemenként' })
    for (let [key, value] of Object.entries(sums?.saleAgentTree || {})) {
      printSlip.Rows.push({ Bold: false, Msg: `${agentTypeNames[key]}` })
      for (let [subKey, subValue] of Object.entries(value || {})) {
        printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`  ${paymentAgents[subKey]?.name?.hu || agentTypeNames[key]}`, `${subValue}Ft`, maxChars) })
      }
    }
    // printSlip.Rows.push({
    //   Bold: false,
    //   Msg: strLeftRight(
    //     ' ' + (item?.count + '').padStart(2, ' ') + ' x ' + ` ${item.packagePrice}Ft/1 ${sizeUnitNames['unit']}`,
    //     `${item.packagePrice * item.count}Ft ${vats['27.00']}00`, maxChars)
    // })

    return printSlip
  }


  const createAggregatedPrintSlip = (aggregatedOrder, options = {}, _orderObj = null) => {
    let {
      printLocationName = true,
      printLocationAddress = true,
      printDeviceName = true,
      printSysUserName = true,
      printItems = true,
      printOrderNumber = true,
      printOrderIdentifier = true,
      printOrderTimestamp = true,
      printNotes = true,
      printQRCode = true,
      printDiscountSummary = true,
      printNonFiscalDisclaimer = true,
      printExtendedTrackingInfo = false,
      maxChars = 48,
      headerPrintSlip = 0,
      footerPrintSlip = 0,
      ccPrintSlip = null,
      figletPrintSlip = null,
      extraPrintSlip = null,
      languageCode = 'hu'
    } = options || {}
    headerPrintSlip = (headerPrintSlip === 0 ? {
      Rows: [
        { Bold: true, Msg: strCenter('ÖSSZESÍTŐ', maxChars) }
      ]
    } : headerPrintSlip)
    footerPrintSlip = (footerPrintSlip === 0 ? {
      Rows: [
        { Bold: true, Msg: strCenter('Köszönjük a vásárlást!', maxChars) }
      ]
    } : footerPrintSlip)
    let {
      deliveryCost,
      items,
      total,
      paymentUUID,
      payment
    } = aggregatedOrder
    let printSlip = { Rows: [] }
    if (printLocationName) {
      let row = { Bold: true, Font: 'double', Msg: strCenter(`${location.name}`, Math.round(maxChars / 2)) }
      printSlip.Rows.push(row)
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, ' ') })
    }
    if (printLocationAddress) {
      printSlip.Rows = printSlip.Rows.concat(formatLocationAddressPrintSlip(maxChars))
      printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, ' ') })
    }
    printSlip.Rows = printSlip.Rows.concat(headerPrintSlip ? headerPrintSlip.Rows : [])

    if (printDeviceName) {
      let row = { Bold: true, Msg: strLeftRight(`Eszköz:`, `${deviceName}`, maxChars) }
      printSlip.Rows.push(row)
    }

    if (printSysUserName && loggedInUser != null) {
      let row = { Bold: true, Msg: strLeftRight(`Felhasználó:`, `${loggedInUser?.firstName}`, maxChars) }
      printSlip.Rows.push(row)
    }

    printSlip.Rows.push({ Bold: true, Msg: '' })
    if (printOrderTimestamp) {
      let row = { Bold: false, Msg: strLeftRight(`Összegzés ideje:`, `${moment().format('YY-MM-DD HH:mm')}`, maxChars) }
      printSlip.Rows.push(row)
    }

    // Print figlet slip - It can be the ordernumber in ascii mode for example.
    if (figletPrintSlip) {
      try {
        printSlip.Rows = printSlip.Rows.concat(figletPrintSlip.Rows)
      } catch (error) {
        console.log('figletPrintSlip error', error)
      }
    }

    if (printItems) {
      try {
        let vatSums = {}
        let orderServiceFees = {}
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        for (let item of items) {
          if (+item.sumCount > 0) {
            vatSums[item.vat] = (vatSums[item.vat] || 0) + +item.sumItemPrice
            vatSums[`service ${item.vat}`] = (vatSums[`service ${item.vat}`] || 0) + +item.sumServiceFee
            let itemLangName = getPreferredLanguage(item?.itemName, languageCode, 'Termék')
            let itemSizeName = getPreferredLanguage(item?.sizeName, languageCode, '')
            let itemName = `${itemLangName} ${itemSizeName}`
            if (+item.sumCount > 1 || itemName.length > (maxChars - 12)) {
              // If the count is greater than 1, print the extra line with the unit multiplier
              printSlip.Rows.push({
                Bold: false,
                Msg: itemName
              })
              printSlip.Rows.push({
                Bold: false,
                Msg: strLeftRight(
                  ('' + item.sumCount).padStart(2, ' ') + ' x ' + (+item.sumItemPrice / +item.sumCount).toFixed(2) + 'Ft/',
                  ` ${item.sumItemPrice}Ft ${vats[item.vat]}00`, maxChars)
              })

            } else {
              printSlip.Rows.push({
                Bold: false,
                Msg: strLeftRight(itemName, `${item.sumItemPrice}Ft ${vats[item.vat]}00`, maxChars)
              })
            }

            // Print item packaging
            if (+item.sumPackagePrice > 0) {
              vatSums['27.00'] = (vatSums['27.00'] || 0) + +item.sumPackagePrice
              let packageName = 'Csomagolás'
              if (item.sumCount > 1) {
                // If the count is greater than 1, print the extra line with the unit multiplier
                printSlip.Rows.push({
                  Bold: false,
                  Msg: packageName
                })
                printSlip.Rows.push({
                  Bold: false,
                  Msg: strLeftRight(
                    ' ' + (item?.sumCount + '').padStart(2, ' ') + ' x ' + ` ${(+item.sumPackagePrice / +item.sumCount).toFixed(2)}Ft`,
                    `${(+item.sumPackagePrice).toFixed(2)}Ft ${vats['27.00']}00`, maxChars)
                })
              } else {
                printSlip.Rows.push({
                  Bold: false,
                  Msg: strLeftRight(packageName, `${(+item.sumPackagePrice).toFixed(2)}Ft ${vats['27.00']}00`, maxChars)
                })
              }
            }
            if (+item.sumDepositPrice > 0) {
              let depositVat = (+item.depositVat || 0).toFixed(2)
              vatSums[depositVat] = (vatSums[depositVat] || 0) + +item.sumDepositPrice
              if (item.count > 1) {
                // If the count is greater than 1, print the extra line with the unit multiplier
                printSlip.Rows.push({
                  Bold: false,
                  Msg: 'Betétdíj'
                })
                printSlip.Rows.push({
                  Bold: false,
                  Msg: strLeftRight(
                    ' ' + (item?.count + '').padStart(2, ' ') + ' x ' + ` ${(+item.sumDepositPrice / +item.sumCount).toFixed(2)}Ft/1 ${sizeUnitNames['piece']}`,
                    `${item.sumDepositPrice}Ft ${vats[depositVat]}00`, maxChars)
                })
              } else {
                printSlip.Rows.push({
                  Bold: false,
                  Msg: strLeftRight('Betétdíj', `${(+item.sumDepositPrice).toFixed(2)}Ft ${vats[depositVat]}00`, maxChars)
                })
              }
            }
          }
        }
        // Print delivery fee
        if (+deliveryCost > 0) {
          vatSums['delivery 27.00'] = (vatSums['delivery 27.00'] || 0) + +deliveryCost
          printSlip.Rows.push({
            Bold: false,
            Msg: strLeftRight(deliveryInfo?.feeName || `Elviteli/Csomagolás díj`, (deliveryCost + `Ft ${vats['27.00']}00`), maxChars)
          })
        }
        // Print total
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows.push({ Bold: true, Msg: strLeftRight(`Összesítve:`, (total.toFixed(2) + 'Ft'), maxChars) })
        // Print payments
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        if (!paymentUUID) {
          printSlip.Rows.push({ Bold: true, Msg: 'Nincs fizetve / Unpaid' })
        } else {
          printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`${paymentBlockNames[payment?.agentType || 'cash']}:`, (+payment?.amount).toFixed(2) + 'Ft', maxChars) })
        }
        // Áfa összegzése
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows.push({ Bold: true, Msg: `Áfa bontás:` })
        for (let [key, value] of Object.entries(vatSums || {})) {
          printSlip.Rows.push({ Bold: false, Msg: strLeftRight(`${key}%`, (value.toFixed(2) + 'Ft'), maxChars) })
        }
      } catch (err) {
        console.log(err)
      }
    }
    // Credit card print slip print if exists
    if (ccPrintSlip) {
      try {
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows = printSlip.Rows.concat(ccPrintSlip.Rows)
      } catch (e) {
        console.log('ccPrintSlip', e)
      }
    }
    // Extra info if needed for example, a promotion
    if (extraPrintSlip) {
      try {
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows = printSlip.Rows.concat(extraPrintSlip.Rows)
      } catch (e) {
        console.log('extraPrintSlip', e)
      }
    }
    // Footer printslip, example: Thank you for shopping at xxx
    if (footerPrintSlip) {
      try {
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows = printSlip.Rows.concat(footerPrintSlip.Rows)
      } catch (e) {
        console.log('footerPrintSlip', e)
      }
    }
    // Needed when the order is printed with a non fiscal printer (simple pos printer)
    if (printNonFiscalDisclaimer) {
      try {
        printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
        printSlip.Rows.push({ Bold: true, Msg: strCenter('Készpénz átvételére nem jogosít', maxChars) })
      } catch (e) {
        console.log('footerPrintSlip', e)
      }
    }
    // Order state tracking qr code
    // if (printExtendedTrackingInfo) {
    //   printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
    //   printSlip.Rows.push({ Bold: true, Msg: 'Rendelésed itt található:' })
    //   printSlip.Rows.push({ Bold: true, Msg: strCenter('https://barsoft.me', maxChars) })
    //   printSlip.Rows.push({ Bold: true, Msg: 'Azonosító kód:' })
    //   printSlip.Rows.push({ Bold: true, Msg: strCenter((orderShortIDFormatted?.length > 0 ? orderShortIDFormatted : orderUUID), maxChars) })
    // } else {
    //   printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
    //   printSlip.Rows.push({ Bold: true, Msg: strCenter('https://barsoft.me/' + (orderShortIDFormatted?.length > 0 ? orderShortID : ''), maxChars) })
    // }
    // if (printQRCode) {
    //   printSlip.Rows.push({ Position: 'center', Type: 'qrcode', Msg: 'https://barsoft.me/' + (orderShortIDFormatted?.length > 0 ? orderShortID : orderUUID) })
    // }
    return printSlip
  }

  const createFiglet = (text) => {
    figlet.parseFont('Standard', figletFont);
    let fig = figlet.textSync(text, {
      font: "Standard",
    });
    return fig
  };

  const createFigletFromOrderNumber = (orderNumber, maxChars) => {
    let printSlip = { Rows: [] };
    //console.log('createTextArray', orderNumber)
    let textarray = createFiglet(orderNumber).split('\n');
    //console.log('textarray', textarray)
    for (let row of textarray) {
      printSlip.Rows = [...printSlip.Rows, { Bold: false, Msg: strCenter(row, maxChars) }];
    }
    return printSlip;
  };

  const createOrderExternalNumberPrintSlip = (order, extOrderNumber, options = {}) => {
    if (order == null) return null
    const {
      maxChars = 48
    } = options
    let {
      orderNumber = '',
      orderIdentifier = '',
      insertDeviceType,
      insertTimestamp
    } = order
    let printSlip = { Rows: [] }
    let figletPrintSlip = createFigletFromOrderNumber(extOrderNumber, maxChars)
    if (orderNumber != null) {
      let messageLeft = `Rendelés: ${orderNumber}`
      let row = { Bold: true, Msg: messageLeft, Font: 'doubleHeight' }
      printSlip.Rows.push(row)
    }
    if (orderIdentifier != null && (orderIdentifier + '').length > 0) {
      let messageLeft = `ID: ${orderIdentifier}`
      let row = { Bold: true, Msg: messageLeft, Font: 'double' }
      printSlip.Rows.push(row)
    }
    if (insertDeviceType != null) {
      let messageLeft = `Eszköz: ${deviceTypeName[insertDeviceType] || 'N/A'}`
      let row = { Bold: true, Msg: messageLeft, Font: 'normal' }
      printSlip.Rows.push(row)
    }
    if (insertTimestamp != null) {
      let messageLeft = `Rendelés ideje: `
      let row = { Bold: false, Msg: messageLeft + `${moment(insertTimestamp).format('YY-MM-DD HH:mm')}`.padStart(maxChars - messageLeft.length, ' '), Font: 'doubleHeight' }
      printSlip.Rows.push(row)
    }
    printSlip.Rows.push({ Bold: true, Msg: ''.padStart(maxChars, '-') })
    printSlip.Rows = printSlip.Rows.concat(figletPrintSlip.Rows)
    return printSlip
  }

  useEffect(() => {
    if (locations && hasDeviceContext && deviceUUID != null) {
      if (loggedIn && deviceBrandID && deviceApiUrl && deviceType && deviceLocationUUID) {
        brandPrinters.getDevicePrinters(deviceApiUrl, deviceLocationUUID, deviceUUID).then(res => {
          if (res.success) {
            setPrinters(res.data)
          }
        })
        if (locations[deviceLocationUUID]) {
          setLocation(locations[deviceLocationUUID])
        }
      }
    }
  }, [hasDeviceContext, loggedIn, deviceBrandID, deviceApiUrl, deviceToken, deviceType, deviceUUID, deviceLocationUUID, locations])

  useEffect(() => {
    printersRef.current = printers
  }, [printers])

  useEffect(() => {
    client?.clientSocket.addCallback('onPrintOrderSlip', uid.current, onPrintOrderSlip)
    client?.clientSocket.addCallback('onPrintersUpdated', uid.current, onPrintersUpdated)
    client?.orders.addCallback('onOrderExternalOrderNumberReceived', uid.current, onOrderExternalOrderNumberReceived)
    return () => {
      client?.clientSocket.removeCallback('onPrintOrderSlip', uid.current)
      client?.clientSocket.removeCallback('onPrintersUpdated', uid.current)
    }
  }, [client])

  return (
    <OkePrintContext.Provider
      value={{
        printers, posPrint, payInType, payOutType, stornoReasonType, showDigital,
        createXReportPrintSlip, printOrderSlips, createPrintSlip, createOrderPrintSlip,
        createExternalPaymentPrintSlip, printOrderItemStornoSlips, printOrderStornoSlips, createAggregatedPrintSlip,
        setExtOrderNumberFiscalPrintCB
      }}
    >
      {props.children}
    </OkePrintContext.Provider>
  )
}

export { OkePrintContext, OkePrintProvider }
